import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { compose } from 'redux';

import SentrySDK from 'common/3rd/SentrySDK';
import { reloadOnMerge } from 'common/actions/postMerge';
import { loadQuery } from 'common/actions/postQueries';
import AJAX from 'common/AJAX';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { OpenModalContext } from 'common/containers/ModalContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import connect from 'common/core/connect';
import AccessModal from 'common/modals/AccessModal';
import MergePostSuggestion from 'common/post/MergePostSuggestion';
import AlertDialog from 'common/uiV3/AlertDialog';
import Button from 'common/uiV3/Button';
import SearchableList from 'common/uiV3/Composite/SearchableList';
import delayer from 'common/util/delayer';
import { getPostQueryKey } from 'common/util/filterPosts';
import hasPermission from 'common/util/hasPermission';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import withContexts from 'common/util/withContexts';

import 'css/components/post/_MergePostDropdown.scss';

const Delay = 300;
const ErrorMessages = {
  'board deleted': 'The board for one of the posts does not exist anymore',
  'invalid company': "You can't merge posts from another company",
  'not authorized': "You don't have permission to merge posts",
  'post missing': 'One of the posts you are trying to merge does not exist anymore',
  'same posts': 'It is not possible to merge one post with itself',
};
const Pages = 3;
const ScoreFactor = 100000;

class MergePostDropdown extends Component {
  static propTypes = {
    board: PropTypes.object,
    boards: PropTypes.array,
    company: PropTypes.object,
    loadQuery: PropTypes.func,
    onPostMerged: PropTypes.func,
    onPostSelected: PropTypes.func,
    openModal: PropTypes.func,
    permissionKey: PropTypes.string,
    placeholder: PropTypes.string,
    post: PropTypes.object,
    postQueries: PropTypes.object,
    reloadData: PropTypes.func,
    viewer: PropTypes.object,
  };

  static defaultProps = {
    permissionKey: 'mergePosts',
  };

  state = {
    error: null,
    merging: false,
    searchValue: null,
    showMergeConfirmModal: false,
    intoPost: null,
    isLoading: true,
  };

  componentDidMount() {
    this._isMounted = true;
    this._onChangeDelayer = new delayer(this.onSearchChangeAfterDelay, Delay);
    this.loadInitialSuggestions();
  }

  loadMergeSuggestions = () => {
    const { boards, post, loadQuery } = this.props;
    return loadQuery({
      boards: this.mapBoardsToQueryKey(boards),
      pages: 1,
      query: 'mergeSuggestions',
      postID: post._id,
    });
  };

  checkPermission = () => {
    const { company, openModal, permissionKey, viewer } = this.props;
    if (permissionKey && !hasPermission(permissionKey, company, viewer)) {
      openModal(AccessModal, {
        requiredPermissions: [permissionKey],
      });
      return false;
    }
    return true;
  };

  loadInitialSuggestions = () => {
    if (!this.checkPermission()) {
      return;
    }

    this.loadMergeSuggestions().then(() => {
      if (this._isMounted) {
        this.setState({ isLoading: false });
      }
    });
  };

  componentWillUnmount() {
    this._isMounted = false;
    this._onChangeDelayer.cancel();
  }

  mapBoardsToQueryKey = (boards) => {
    return boards.map((board) => board.urlName).join('_');
  };

  onSearchChange = (searchValue) => {
    const value = searchValue.trim();
    this._onChangeDelayer.cancel();

    if (!value) {
      this.setState({ searchValue: null, isLoading: true });
      this.loadMergeSuggestions().then(() => {
        if (this._isMounted) {
          this.setState({ isLoading: false });
        }
      });
      return;
    }

    this.setState({ isLoading: true });
    this._onChangeDelayer.callAfterDelay(value);
  };

  onSearchChangeAfterDelay = (value) => {
    const { boards, loadQuery } = this.props;
    loadQuery({
      boards: this.mapBoardsToQueryKey(boards),
      pages: Pages,
      scoreFactor: ScoreFactor,
      textSearch: value,
    }).then(() => {
      if (this._isMounted) {
        this.setState({
          searchValue: value,
          isLoading: false,
        });
      }
    });
  };

  onOptionSelect = (option) => {
    const { posts } = this.props;
    // Extract post ID from option value (format: "boardID:postURLName")
    const [boardID, postURLName] = option.value.split(':');
    const intoPost = posts[boardID]?.[postURLName];

    if (!intoPost) {
      return;
    }

    if (this.props.onPostSelected) {
      this.props.onPostSelected(intoPost);
      return;
    }

    this.setState({
      showMergeConfirmModal: true,
      intoPost,
      error: null,
    });
  };

  onMergePost = async (intoPost) => {
    const { merging } = this.state;
    if (merging) {
      return;
    }

    this.setState({ merging: true });

    const { boards, board: fromBoard, onPostMerged, post, reloadData } = this.props;
    const response = await AJAX.post('/api/posts/merge', {
      mergePostID: post._id,
      mergePostIntoID: intoPost._id,
    });

    const { error } = parseAPIResponse(response, {
      errors: ErrorMessages,
      isSuccessful: isDefaultSuccessResponse,
    });

    if (error) {
      this.props.showToast(error.message, ToastTypes.error);
      this.setState({
        merging: false,
      });
      return;
    }

    const toBoard = boards.find((board) => board._id === intoPost.boardID);
    await reloadData(post, intoPost, fromBoard, toBoard);

    if (this._isMounted) {
      this.setState({
        merging: false,
        showMergeConfirmModal: false,
        intoPost: null,
      });
    }

    onPostMerged(intoPost, toBoard, post);
  };

  renderMergeConfirmModal() {
    const { showMergeConfirmModal, intoPost, merging } = this.state;
    const { boards, board } = this.props;

    const toBoard = intoPost ? boards.find((b) => b._id === intoPost.boardID) : null;
    const isTurningPublic = board?.settings?.private && !toBoard?.settings?.private;

    return (
      <AlertDialog
        open={showMergeConfirmModal}
        onOpenChange={(open) => this.setState({ showMergeConfirmModal: open })}>
        <AlertDialog.Content onCloseAutoFocus={() => this.setState({ intoPost: null })}>
          <AlertDialog.Header>
            <AlertDialog.Title>Merge post</AlertDialog.Title>
            <AlertDialog.Description>
              Are you sure you'd like to merge this post into "{intoPost?.title}"? All votes and
              comments will be moved.
            </AlertDialog.Description>
            {isTurningPublic && (
              <AlertDialog.Description className="!font-semibold">
                Warning: You are merging a post from a private board to a public board. All comments
                and votes will be made public.
              </AlertDialog.Description>
            )}
          </AlertDialog.Header>
          <AlertDialog.Footer>
            <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
            <Button
              type="button"
              isLoading={merging}
              disabled={!intoPost}
              onClick={() => intoPost && this.onMergePost(intoPost)}>
              Merge
            </Button>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    );
  }

  getOptions() {
    const { company, boards, post, postQueries, posts } = this.props;
    const { searchValue } = this.state;

    const queryKey = searchValue
      ? getPostQueryKey({
          boards: this.mapBoardsToQueryKey(boards),
          scoreFactor: ScoreFactor,
          textSearch: searchValue,
        })
      : getPostQueryKey({
          boards: this.mapBoardsToQueryKey(boards),
          query: 'mergeSuggestions',
          postID: post._id,
        });

    const queryResult = postQueries[queryKey];
    if (!queryResult?.posts?.length) {
      return [];
    }

    return queryResult.posts
      .map((postResult) => {
        const { boardID, postURLName } = postResult;
        const postSuggestion = posts[boardID]?.[postURLName];

        if (!postSuggestion || post._id === postSuggestion._id) {
          return null;
        }

        const status = company.statuses.find((s) => s.name === postSuggestion.status);
        if (!status) {
          SentrySDK.captureException(
            new Error(`Statuses can't be found for company ${company._id}`),
          );
          return null;
        }

        const board = boards.find((b) => b._id === boardID);
        if (!board) {
          return null;
        }

        const optionValue = `${boardID}:${postURLName}`;
        const postWithBoard = { ...postSuggestion, board };

        return {
          value: optionValue,
          label: (
            <MergePostSuggestion
              onTap={(e) => {
                if (e.metaKey || e.ctrlKey) {
                  window.open(`/admin/feedback/${board.urlName}/p/${postURLName}`, '_blank');
                  return;
                }
                this.onOptionSelect({ value: optionValue });
              }}
              post={postWithBoard}
            />
          ),
          searchLabel: postSuggestion.title,
        };
      })
      .filter(Boolean);
  }

  render() {
    const { placeholder } = this.props;
    const { merging, isLoading, searchValue } = this.state;

    const getSearchPlaceholder = () => {
      if (placeholder) {
        return placeholder;
      } else if (merging) {
        return 'Merging...';
      } else {
        return 'Search post to merge into\u2026';
      }
    };

    const options = this.getOptions();
    const emptyPlaceholder = searchValue ? 'No posts found' : 'No posts to merge into';

    return (
      <div className="mergePostDropdown max-w-[300px]">
        <SearchableList
          options={options}
          isSearchable
          isLoading={isLoading}
          searchPlaceholder={getSearchPlaceholder()}
          emptyPlaceholder={emptyPlaceholder}
          onSearchChange={this.onSearchChange}
          onOptionSelect={this.onOptionSelect}
          className={merging ? 'opacity-50 pointer-events-none' : ''}
        />
        {this.renderMergeConfirmModal()}
      </div>
    );
  }
}

export default compose(
  connect(
    (state) => ({
      posts: state.posts,
      postQueries: state.postQueries,
    }),
    {
      loadQuery,
      reloadData: reloadOnMerge,
    },
  ),
  withContexts(
    {
      company: CompanyContext,
      openModal: OpenModalContext,
      viewer: ViewerContext,
      showToast: ShowToastContext,
    },
    {
      forwardRef: true,
    },
  ),
)(MergePostDropdown);
